import {
  Box,
  Button,
  ClickAwayListener,
  Grow,
  InputAdornment,
  Paper,
  Popper,
  TextField,
} from '@material-ui/core'
import { useEffect, useRef, useState } from 'react'
import { MdDateRange } from 'react-icons/md'
import { DateRangePicker } from 'shared'

interface IDateRangerPicker {
  onSelectedDate: (params: ISelectDate) => void
  defaultStartDate?: Date
  defaultEndDate?: Date
  startDate?: Date
  endDate?: Date
  classContainer?: any
  isUniqueDate?: boolean | null | undefined
  title?: string
  isScheduledDate?: boolean
  allowEmpty?: boolean
  actionLabel?: string
}

export type ISelectDate = {
  startDate: Date
  endDate: Date
  isUniqueDate?: boolean | null | undefined
}

const getYesterday = () => {
  const date = new Date()
  const hour = date.getHours()
  date.setDate(date.getDate() - (hour >= 0 && hour < 12 ? 2 : 1))
  return date
}

const DateRangerPicker = (props: IDateRangerPicker) => {
  const [selectedSearchDateFrom, setSelectedSearchDateFrom] = useState<Date | null>(
    props.startDate || props.defaultStartDate || (props.allowEmpty ? null : getYesterday()),
  )
  const [selectedSearchDateTo, setSelectedSearchDateTo] = useState<Date | null>(
    props.endDate || props.defaultEndDate || (props.allowEmpty ? null : getYesterday()),
  )
  const [open, setOpen] = useState(false)
  const anchorRef = useRef<HTMLDivElement>(null)
  const anchorEl = useRef(null)
  const prevOpen = useRef(open)
  const startDateTime = props.startDate?.getTime()
  const endDateTime = props.endDate?.getTime()

  const handleChangeDate = async (startDate: Date, endDate: Date) => {
    setSelectedSearchDateFrom(startDate)
    setSelectedSearchDateTo(endDate)
  }

  const handleToggle = () => {
    setOpen(prevOpen => !prevOpen)
  }

  const handleClose = (event: any) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return
    }

    setOpen(false)
  }

  const onFilter = () => {
    const startDate = selectedSearchDateFrom || new Date()
    const endDate = selectedSearchDateTo || startDate

    props.onSelectedDate({
      startDate,
      endDate,
      isUniqueDate: props?.isUniqueDate,
    })
    setOpen(false)
  }

  useEffect(() => {
    if (prevOpen.current && !open) anchorRef?.current?.focus()
    prevOpen.current = open
  }, [open])

  useEffect(() => {
    if (props.allowEmpty) {
      setSelectedSearchDateFrom(startDateTime === undefined ? null : new Date(startDateTime))
      setSelectedSearchDateTo(endDateTime === undefined ? null : new Date(endDateTime))
      return
    }

    if (startDateTime !== undefined) {
      setSelectedSearchDateFrom(new Date(startDateTime))
    }
    if (endDateTime !== undefined) {
      setSelectedSearchDateTo(new Date(endDateTime))
    }
  }, [props.allowEmpty, startDateTime, endDateTime])

  return (
    <Box>
      <Box className={props.classContainer || {}}>
        {props.isUniqueDate && (
          <TextField
            ref={anchorRef}
            variant="outlined"
            fullWidth
            value={selectedSearchDateFrom?.toLocaleDateString() ?? ''}
            onClick={handleToggle}
            InputProps={{
              readOnly: true,
              startAdornment: (
                <InputAdornment position="start">
                  <MdDateRange fontSize="1.2rem" color="#757575" />
                </InputAdornment>
              ),
              style: { cursor: 'pointer' },
            }}
            style={{ cursor: 'pointer' }}
          />
        )}
        {!props.isUniqueDate && (
          <TextField
            ref={anchorRef}
            variant="outlined"
            size="small"
            label={props.title ?? 'Período'}
            fullWidth
            value={
              selectedSearchDateFrom && selectedSearchDateTo
                ? `${selectedSearchDateFrom.toLocaleDateString()} - ${selectedSearchDateTo.toLocaleDateString()}`
                : ''
            }
            onClick={handleToggle}
            InputProps={{
              readOnly: true,
              startAdornment: (
                <InputAdornment position="start">
                  <MdDateRange fontSize="1.2rem" color="#757575" />
                </InputAdornment>
              ),
              style: { cursor: 'pointer' },
            }}
            style={{ cursor: 'pointer', width: '250px' }}
          />
        )}
        <Popper
          open={open}
          anchorEl={anchorRef.current}
          role={undefined}
          transition
          disablePortal
          style={{ zIndex: 2 }}
          placement="top-start"
        >
          {({ TransitionProps, placement }) => (
            <Grow
              {...TransitionProps}
              style={{
                transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
              }}
            >
              <Paper>
                <ClickAwayListener onClickAway={handleClose}>
                  <Box>
                    <DateRangePicker
                      startDate={selectedSearchDateFrom || new Date()}
                      endDate={selectedSearchDateTo || new Date()}
                      anchorEl={anchorEl.current}
                      onChange={event =>
                        handleChangeDate(
                          event.selection.startDate || new Date(),
                          event.selection.endDate || new Date(),
                        )
                      }
                      isScheduledDate={props.isScheduledDate}
                    />
                    <Box
                      style={{
                        textAlign: 'end',
                        maxWidth: '20.75rem',
                        padding: '0 0.833em 1rem 0',
                      }}
                    >
                      <Button variant="contained" color="primary" onClick={() => onFilter()}>
                        {props.actionLabel || 'Filtrar'}
                      </Button>
                    </Box>
                  </Box>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>
      </Box>
    </Box>
  )
}

export default DateRangerPicker
